/**
 * DOM Actions
 * 
 * Browser-side tool implementations for visual assistance:
 * - highlightElement: Visual highlight with glow effect
 * - scrollTo: Smooth scroll to element
 * - navigateTo: Same-origin navigation
 * - fillForm: Fill form fields (with safety checks)
 * - clickElement: Click buttons/links (with confirmation)
 */

import { isSensitiveField } from './sensitive-filter.js';

// ============================================================================
// HIGHLIGHT ELEMENT
// ============================================================================

/**
 * Highlight an element with a glowing visual effect
 * 
 * @param {Object} options
 * @param {string} options.selector - CSS selector for the element
 * @param {string} [options.color='#6366f1'] - Highlight color (hex)
 * @param {number} [options.duration=3000] - Duration in milliseconds
 * @returns {Promise<Object>} - Result with element info
 */
export async function highlightElement(options = {}) {
  const { 
    selector, 
    color = '#6366f1', 
    duration = 3000 
  } = options;
  
  if (!selector) {
    return { success: false, error: 'Selector is required' };
  }
  
  // Find element
  const element = document.querySelector(selector);
  if (!element) {
    return { success: false, error: `Element not found: ${selector}` };
  }
  
  // Scroll into view first
  element.scrollIntoView({ 
    behavior: 'smooth', 
    block: 'center',
    inline: 'nearest'
  });
  
  // Wait for scroll to complete
  await sleep(400);
  
  // Get element position
  const rect = element.getBoundingClientRect();
  
  // Inject CSS if not already present
  injectHighlightStyles();
  
  // Create overlay element
  const overlay = document.createElement('div');
  overlay.className = 'ttp-highlight-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: ${rect.top - 4}px;
    left: ${rect.left - 4}px;
    width: ${rect.width + 8}px;
    height: ${rect.height + 8}px;
    --ttp-highlight-color: ${color};
    border-color: ${color};
    box-shadow: 0 0 20px ${color}80, 0 0 40px ${color}40;
    pointer-events: none;
    z-index: 2147483647;
  `;
  
  document.body.appendChild(overlay);
  
  // Remove after duration
  setTimeout(() => {
    overlay.classList.add('ttp-fade-out');
    setTimeout(() => {
      overlay.remove();
    }, 300);
  }, duration);
  
  return {
    success: true,
    selector,
    elementInfo: {
      tag: element.tagName.toLowerCase(),
      id: element.id || null,
      text: element.innerText?.trim().substring(0, 100) || null,
      position: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      }
    }
  };
}

/**
 * Inject highlight CSS styles into the document
 */
function injectHighlightStyles() {
  if (document.getElementById('ttp-highlight-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'ttp-highlight-styles';
  style.textContent = `
    @keyframes ttp-pulse {
      0%, 100% {
        box-shadow: 0 0 20px var(--ttp-highlight-color, #6366f1)80,
                    0 0 40px var(--ttp-highlight-color, #6366f1)40;
      }
      50% {
        box-shadow: 0 0 30px var(--ttp-highlight-color, #6366f1)99,
                    0 0 60px var(--ttp-highlight-color, #6366f1)60;
      }
    }
    
    .ttp-highlight-overlay {
      border: 3px solid var(--ttp-highlight-color, #6366f1);
      border-radius: 8px;
      animation: ttp-pulse 1s ease-in-out infinite;
      transition: opacity 0.3s ease;
    }
    
    .ttp-highlight-overlay.ttp-fade-out {
      opacity: 0;
    }
    
    .ttp-fill-success {
      outline: 3px solid #22c55e !important;
      outline-offset: 2px;
      transition: outline 0.3s ease;
    }
  `;
  document.head.appendChild(style);
}

// ============================================================================
// SCROLL TO
// ============================================================================

/**
 * Scroll to an element or position
 * 
 * @param {Object} options
 * @param {string} [options.selector] - CSS selector (takes precedence)
 * @param {number} [options.x] - X position (if no selector)
 * @param {number} [options.y] - Y position (if no selector)
 * @param {string} [options.direction] - 'up' or 'down' for relative scrolling
 * @param {number} [options.amount] - Pixels to scroll (for relative scrolling, default: 500)
 * @param {string} [options.position='center'] - 'center', 'top', 'bottom'
 * @param {string} [options.behavior='smooth'] - 'smooth' or 'instant'
 * @returns {Promise<Object>} - Result
 */
export async function scrollTo(options = {}) {
  const { 
    selector, 
    x, 
    y, 
    direction,
    amount = 500,
    position = 'center',
    behavior = 'smooth' 
  } = options;
  
  // Scroll to element (highest priority)
  if (selector) {
    const element = document.querySelector(selector);
    if (!element) {
      return { success: false, error: `Element not found: ${selector}` };
    }
    
    const block = position === 'top' ? 'start' : 
                  position === 'bottom' ? 'end' : 'center';
    
    element.scrollIntoView({ 
      behavior, 
      block,
      inline: 'nearest'
    });
    
    // Wait for scroll
    await sleep(behavior === 'smooth' ? 500 : 100);
    
    const rect = element.getBoundingClientRect();
    
    return { 
      success: true, 
      scrollType: 'element',
      selector,
      elementPosition: {
        top: rect.top,
        left: rect.left
      }
    };
  }
  
  // Relative scrolling (scroll up/down)
  if (direction === 'up' || direction === 'down') {
    // Ensure amount has a default value (should be set by destructuring, but be defensive)
    const scrollAmount = amount || 500;
    
    const currentY = window.scrollY;
    const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
    
    let newY;
    if (direction === 'up') {
      newY = Math.max(0, currentY - scrollAmount);
    } else {
      // direction === 'down'
      newY = Math.min(maxScrollY, currentY + scrollAmount);
    }
    
    window.scrollTo({ 
      top: newY, 
      left: window.scrollX, 
      behavior 
    });
    
    await sleep(behavior === 'smooth' ? 500 : 100);
    
    return { 
      success: true, 
      scrollType: 'relative',
      direction,
      amount: scrollAmount,
      scrolledFrom: { y: currentY },
      scrolledTo: { y: window.scrollY },
      atTop: window.scrollY === 0,
      atBottom: window.scrollY >= maxScrollY - 1 // Allow 1px tolerance
    };
  }
  
  // Absolute scroll to position
  if (x !== undefined || y !== undefined) {
    window.scrollTo({ 
      top: y ?? window.scrollY, 
      left: x ?? window.scrollX, 
      behavior 
    });
    
    await sleep(behavior === 'smooth' ? 500 : 100);
    
    return { 
      success: true, 
      scrollType: 'position',
      scrolledTo: { 
        x: x ?? window.scrollX, 
        y: y ?? window.scrollY 
      }
    };
  }
  
  return { success: false, error: 'Either selector, x/y position, or direction (up/down) required' };
}

// ============================================================================
// NAVIGATE TO
// ============================================================================

/**
 * Navigate to a URL (same-origin only for security)
 * 
 * @param {Object} options
 * @param {string} options.url - URL to navigate to
 * @returns {Promise<Object>} - Result
 */
export async function navigateTo(options = {}) {
  const { url } = options;
  
  if (!url) {
    return { success: false, error: 'URL is required' };
  }
  
  // Parse and validate URL
  let targetUrl;
  try {
    // Handle relative URLs
    targetUrl = new URL(url, window.location.origin);
  } catch (e) {
    return { success: false, error: `Invalid URL: ${url}` };
  }
  
  // Block dangerous protocols
  if (!['http:', 'https:'].includes(targetUrl.protocol)) {
    return { 
      success: false, 
      error: `Invalid protocol: ${targetUrl.protocol}` 
    };
  }
  
  // Check if external origin - open in new tab instead
  if (targetUrl.origin !== window.location.origin) {
    // External site - open in new tab
    try {
      window.open(targetUrl.href, '_blank', 'noopener,noreferrer');
      return { 
        success: true, 
        openedInNewTab: true,
        navigatedTo: targetUrl.href,
        message: 'Opened external link in a new tab'
      };
    } catch (e) {
      return { 
        success: false, 
        error: 'Failed to open external link in new tab: ' + e.message,
        attempted: targetUrl.href
      };
    }
  }
  
  // Navigate (same-origin)
  const previousUrl = window.location.href;
  window.location.href = targetUrl.href;
  
  return { 
    success: true, 
    navigatedTo: targetUrl.href,
    from: previousUrl
  };
}

// ============================================================================
// FILL FORM
// ============================================================================

/**
 * Fill a form field with a value
 * NEVER fills sensitive fields (passwords, credit cards, etc.)
 * 
 * @param {Object} options
 * @param {string} options.selector - CSS selector for the form field
 * @param {string} options.value - Value to fill
 * @param {boolean} [options.triggerEvents=true] - Trigger input/change events
 * @returns {Promise<Object>} - Result
 */
export async function fillForm(options = {}) {
  const { selector, value, triggerEvents = true } = options;
  
  if (!selector) {
    return { success: false, error: 'Selector is required' };
  }
  
  if (value === undefined || value === null) {
    return { success: false, error: 'Value is required' };
  }
  
  // Find element
  const element = document.querySelector(selector);
  if (!element) {
    return { success: false, error: `Element not found: ${selector}` };
  }
  
  // Validate it's a form field
  const tagName = element.tagName.toLowerCase();
  if (!['input', 'textarea', 'select'].includes(tagName)) {
    return { 
      success: false, 
      error: `Element is not a form field: ${tagName}` 
    };
  }
  
  // SECURITY: Check if field is sensitive
  if (isSensitiveField(element)) {
    return { 
      success: false, 
      error: 'Cannot fill sensitive fields (password, credit card, etc.)',
      blocked: true,
      fieldType: element.type,
      fieldName: element.name
    };
  }
  
  // Scroll into view
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  await sleep(300);
  
  // Focus the field
  element.focus();
  
  // Fill based on element type
  if (tagName === 'select') {
    // For select, find matching option
    const options = Array.from(element.options);
    const match = options.find(opt => 
      opt.value === value || 
      opt.text.toLowerCase() === value.toLowerCase()
    );
    
    if (match) {
      element.value = match.value;
    } else {
      element.value = value;
    }
  } else if (element.type === 'checkbox' || element.type === 'radio') {
    // For checkbox/radio, set checked state
    const shouldCheck = value === true || value === 'true' || value === '1';
    element.checked = shouldCheck;
  } else {
    // For input/textarea
    element.value = value;
  }
  
  // Trigger events for React/Vue/Angular
  if (triggerEvents) {
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
    
    // For React specifically
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value'
    )?.set;
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(element, value);
      element.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
  
  // Visual feedback
  injectHighlightStyles();
  element.classList.add('ttp-fill-success');
  setTimeout(() => {
    element.classList.remove('ttp-fill-success');
  }, 2000);
  
  return {
    success: true,
    selector,
    fieldType: element.type || tagName,
    fieldName: element.name || null,
    // Don't return the actual value for privacy
    filled: true
  };
}

// ============================================================================
// CLICK ELEMENT
// ============================================================================

/**
 * Click an element (button, link, etc.)
 * 
 * @param {Object} options
 * @param {string} options.selector - CSS selector for the element
 * @param {boolean} [options.waitForNavigation=false] - Wait for page navigation
 * @returns {Promise<Object>} - Result
 */
export async function clickElement(options = {}) {
  const { selector, waitForNavigation = false, openInNewTab = false, isExternal = false, href = null } = options;
  
  if (!selector) {
    return { success: false, error: 'Selector is required' };
  }
  
  // Find element
  const element = document.querySelector(selector);
  if (!element) {
    return { success: false, error: `Element not found: ${selector}` };
  }
  
  // Check if element is visible
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return { success: false, error: 'Element is not visible (zero size)' };
  }
  
  // Check if disabled
  if (element.disabled) {
    return { success: false, error: 'Element is disabled' };
  }
  
  // Scroll into view
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  await sleep(300);
  
  // Brief highlight before click
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: ${rect.top - 2}px;
    left: ${rect.left - 2}px;
    width: ${rect.width + 4}px;
    height: ${rect.height + 4}px;
    border: 2px solid #22c55e;
    border-radius: 4px;
    pointer-events: none;
    z-index: 2147483647;
    transition: opacity 0.2s;
  `;
  document.body.appendChild(overlay);
  
  await sleep(200);
  overlay.remove();
  
  // Handle external links - open in new tab
  if (openInNewTab || isExternal) {
    const targetUrl = href || element.href || (element.tagName === 'A' ? element.getAttribute('href') : null);
    if (targetUrl && targetUrl !== '#' && !targetUrl.startsWith('javascript:')) {
      try {
        // Open in new tab
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
        return {
          success: true,
          selector,
          clicked: true,
          openedInNewTab: true,
          url: targetUrl,
          elementInfo: {
            tag: element.tagName.toLowerCase(),
            text: element.innerText?.trim().substring(0, 50) || null
          },
          message: 'Opened link in a new tab'
        };
      } catch (e) {
        console.warn('[TTP] Failed to open in new tab:', e);
        // Fall through to regular click
      }
    }
  }
  
  // Handle navigation tracking
  if (waitForNavigation) {
    const currentUrl = window.location.href;
    
    // Set up navigation promise
    const navigationPromise = new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve({ navigated: false });
      }, 5000);
      
      const handler = () => {
        clearTimeout(timeout);
        window.removeEventListener('beforeunload', handler);
        resolve({ navigated: true });
      };
      
      window.addEventListener('beforeunload', handler);
    });
    
    // Click
    element.click();
    
    // Wait for navigation
    const navResult = await navigationPromise;
    
    return {
      success: true,
      selector,
      clicked: true,
      elementInfo: {
        tag: element.tagName.toLowerCase(),
        text: element.innerText?.trim().substring(0, 50) || null
      },
      navigated: navResult.navigated || window.location.href !== currentUrl
    };
  }
  
  // Simple click without waiting
  element.click();
  
  return {
    success: true,
    selector,
    clicked: true,
    elementInfo: {
      tag: element.tagName.toLowerCase(),
      text: element.innerText?.trim().substring(0, 50) || null
    }
  };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Sleep for specified milliseconds
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Remove all highlight overlays (cleanup)
 */
export function clearHighlights() {
  document.querySelectorAll('.ttp-highlight-overlay').forEach(el => el.remove());
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  highlightElement,
  scrollTo,
  navigateTo,
  fillForm,
  clickElement,
  clearHighlights
};
